import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { addBloodPressure, addWeight, addBloodSugar, addLabData, addHbA1c } from '../services/labService';
import { getProfile } from '../services/profileService';
import apiClient from '../services/api';
import { getMemberLabData } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';

const DailyHealthRecordContainer = styled.div`
  min-height: 100vh;
  background-color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;


const Content = styled.div`
  padding: 0;
  max-width: 100%;
  margin: 0;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: normal;
  color: white;
  background-color: #00BBD3;
  text-align: left;
  margin: 0 0 40px 0;
  padding: 16px 24px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  color: #00BBD3;
  font-size: 16px;
  cursor: pointer;
  padding: 8px 24px;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;

  &:hover {
    opacity: 0.7;
  }
`;

const InstructionSection = styled.div`
  padding: 20px 40px;
  margin-bottom: 32px;
`;

const InstructionText = styled.p`
  font-size: 16px;
  color: #666666;
  margin: 0;
  line-height: 1.5;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CheckMark = styled.span`
  color: #00BBD3;
  font-size: 18px;
  font-weight: bold;
`;

const FormSection = styled.div`
  padding: 0 40px;
  margin-bottom: 32px;
`;

const DateTimeSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #00BBD3;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const DatePickerContainer = styled.div`
  margin-bottom: 24px;
`;

const DateInput = styled.input`
  width: 100%;
  max-width: 300px;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    font-size: 18px;
  }
`;

const TimePickerContainer = styled.div`
  margin-bottom: 32px;
`;

const TimeInput = styled.input`
  width: 100%;
  max-width: 200px;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    font-size: 18px;
  }
`;

const CheckboxSection = styled.div`
  margin-bottom: 32px;
`;

const CheckboxGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-radius: 8px;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
  
  &:hover {
    transform: translateX(4px);
    background-color: rgba(0, 187, 211, 0.02);
    padding: 12px 8px;
  }
`;

const CheckboxItemWithSub = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-radius: 8px;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
  
  &:hover {
    transform: translateX(4px);
    background-color: rgba(0, 187, 211, 0.02);
    padding: 12px 8px;
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  border: 2px solid #00BBD3;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: scale(1);
  box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 0 4px rgba(0, 187, 211, 0.1);
    border-color: #009bb3;
  }
  
  &:checked {
    background-color: #00BBD3;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e");
    background-size: 12px;
    background-position: center;
    background-repeat: no-repeat;
    transform: scale(1.15);
    animation: checkPulse 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @keyframes checkPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0.4);
    }
    50% {
      transform: scale(1.2);
      box-shadow: 0 0 0 8px rgba(0, 187, 211, 0.2);
    }
    100% {
      transform: scale(1.15);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
    }
  }
`;

const CheckboxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  color: #666666;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  flex: 1;
`;

const SubCheckboxContainer = styled.div`
  margin-left: 0px;
  margin-top: 12px;
  display: grid;
  gap: 8px;
  max-height: ${props => props.$show ? 'none' : '0'};
  overflow: ${props => props.$show ? 'visible' : 'hidden'};
  opacity: ${props => props.$show ? '1' : '0'};
  transform: translateY(${props => props.$show ? '0' : '-10px'});
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  width: 100%;
  will-change: opacity, transform;
  backface-visibility: hidden;
`;

const SubCheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`;

const SubCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  border: 2px solid #00BBD3;
  border-radius: 3px;
  background-color: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: scale(1);
  box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 0 3px rgba(0, 187, 211, 0.1);
    border-color: #009bb3;
  }
  
  &:checked {
    background-color: #00BBD3;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e");
    background-size: 10px;
    background-position: center;
    background-repeat: no-repeat;
    transform: scale(1.1);
    animation: subCheckPulse 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:active {
    transform: scale(0.9);
  }
  
  @keyframes subCheckPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0.3);
    }
    50% {
      transform: scale(1.15);
      box-shadow: 0 0 0 6px rgba(0, 187, 211, 0.15);
    }
    100% {
      transform: scale(1.1);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
    }
  }
`;

const SubCheckboxLabel = styled.label`
  font-size: 14px;
  color: #666666;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const RiskAssessmentSection = styled.div`
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const RiskTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #00BBD3;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const RiskSubtitle = styled.p`
  font-size: 14px;
  color: #666666;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const RiskCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const RiskCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  border: 2px solid #00BBD3;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  appearance: none;
  
  &:checked {
    background-color: #00BBD3;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e");
    background-size: 12px;
    background-position: center;
    background-repeat: no-repeat;
  }
`;

const RiskLabel = styled.label`
  font-size: 16px;
  color: #00BBD3;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;
`;

const RiskSubForm = styled.div`
  margin-left: 32px;
  display: grid;
  gap: 12px;
  max-height: ${props => props.show ? 'none' : '0'};
  overflow: ${props => props.show ? 'visible' : 'hidden'};
  opacity: ${props => props.show ? '1' : '0'};
  transition: all 0.3s ease;
  margin-bottom: 40px;
`;

const ImageUploadContainer = styled.div`
  margin-bottom: 24px;
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #999999;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #666666;
    background-color: #f0f0f0;
  }
`;

const UploadIcon = styled.div`
  width: 47px;
  height: 47px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadText = styled.div`
  color: #000000;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  margin-bottom: 16px;
  font-weight: normal;
`;

const BrowseButton = styled.button`
  background-color: white;
  color: #000000;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  
  &:hover {
    border-color: #999999;
    background-color: #f8f9fa;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 16px;
`;

const ImagePreviewItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f0f0f0;
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageRemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background-color: rgba(255, 71, 87, 0.9);
  color: white;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  
  &:hover {
    background-color: #ff4757;
  }
`;

const FoodFormSection = styled.div`
  margin-bottom: 16px;
`;

const FoodFormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #666666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FoodInput = styled.input`
  width: calc(100% - 32px);
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const FoodTextarea = styled.textarea`
  width: calc(100% - 32px);
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const FoodSelect = styled.select`
  width: 200px;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const RadioGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
  margin-top: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const RadioInput = styled.input.attrs({ type: 'radio' })`
  width: 16px;
  height: 16px;
  accent-color: #00BBD3;
`;

const SaveButton = styled.button`
  background-color: #00BBD3;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 18px 32px;
  font-size: 18px;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);
  margin: 20px 40px;
  max-width: calc(100% - 80px);

  &:hover {
    background-color: #009bb3;
  }

  &:active {
    background-color: #008aa0;
  }
`;

const DailyHealthRecord = () => {
  const navigate = useNavigate();

  const now = new Date();
  const currentDate = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
  const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [selectedTime, setSelectedTime] = useState(currentTime);
  const [checkedItems, setCheckedItems] = useState({
    bloodPressure: false,
    bloodSugar: false,
    weightHeight: false,
    food: false,
    mood: false,
    symptoms: false,
    exercise: false,
    sleep: false,
    smokeDrink: false,
    cvRisk: false,
    ckdRisk: false
  });
  
  const [symptomSubItems, setSymptomSubItems] = useState({
    blackouts: false,
    dizzy: false,
    headache: false,
    chestTightness: false,
    legSwelling: false,
    decreasedUrination: false,
    palpitations: false,
    handShaking: false,
    sweating: false,
    extremeHunger: false,
    frequentUrination: false,
    excessiveThirst: false,
    backPain: false,
    littleUrine: false,
    foamyUrine: false,
    chestPain: false,
    breathingDifficulty: false,
    easyFatigue: false,
    others: false,
    othersText: ''
  });
  
  const [cvRiskSubItems, setCvRiskSubItems] = useState({
    age: false,
    gender: false,
    smoking: false,
    diabetes: false,
    bloodPressureValue: false
  });
  
  const [ckdRiskSubItems, setCkdRiskSubItems] = useState({
    age: false,
    gender: false,
    diabetes: false,
    hypertension: false,
    heartDisease: false
  });

  const [bloodPressureData, setBloodPressureData] = useState({
    systolic: '',
    diastolic: '',
    pulse: '',
    timePeriod: 'bedtime'
  });

  const [bloodSugarData, setBloodSugarData] = useState({
    nonNpo: '',
    fasting: '',
    hba1c: ''
  });

  const [weightHeightData, setWeightHeightData] = useState({
    weight: '',
    height: '',
    waist: '',
    bodyFat: ''
  });

  const [sleepData, setSleepData] = useState({
    duration: '',
    quality: '',
    problems: ''
  });

  const [smokeDrinkData, setSmokeDrinkData] = useState({
    alcohol: '',
    smoking: ''
  });

  const [exerciseData, setExerciseData] = useState({
    type: '',
    otherType: '',
    duration: '',
    intensity: '',
    feeling: '',
    otherFeeling: '',
    abnormalSymptoms: '',
    abnormalSymptomsDetail: ''
  });

  const [foodData, setFoodData] = useState({
    images: [],
    name: '',
    description: '',
    mealType: 'มื้อหลัก',
    carbs: '',
    sweetnessLevel: '',
    saltinessLevel: '',
    oilinessLevel: ''
  });

  const [moodData, setMoodData] = useState({
    happiness: '',      
    sadness: '',        
    anxiety: '',        
    anger: '',         
    fatigue: '',        
    other: '',          
    moodFactors: {
      situation: false,           
      healthChange: false,        
      socialInteraction: false,   
      environment: false,         
      otherFactor: false          
    },
    moodFactorsInput: {
      situation: '',
      healthChange: '',
      socialInteraction: '',
      environment: '',
      otherFactor: ''
    },
    moodImpactDescription: ''
  });

  // ดึงส่วนสูงล่าสุดจากโปรไฟล์มาเป็นค่า default
  useEffect(() => {
    const loadProfileHeight = async () => {
      try {
        const profile = await getProfile();
        let height = profile?.height || profile?.data?.height;

        // ถ้า ProfileGet ส่ง height เป็น "0" หรือไม่มี ให้ดึงจาก MemberLabList API
        if (!height || height === '0' || height === 0) {
          try {
            const memberData = await getMemberLabData();
            const labHeight = memberData?.labData?.['height']?.[0]?.value;
            if (labHeight) height = labHeight;
          } catch (labError) {
            console.warn('⚠️ DailyHealthRecord - Could not fetch height from MemberLabList:', labError);
          }
        }

        if (height && height !== '0') {
          setWeightHeightData(prev => ({ ...prev, height: height }));
        }
      } catch (error) {
        console.log('ไม่สามารถดึงข้อมูลส่วนสูงได้:', error.message);
      }
    };
    loadProfileHeight();
  }, []);

  const getMoodSVG = (moodType, level) => {
    const svgs = {
      default: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 42 42" fill="none">
          <path d="M6.85751 35.1425C3.08053 31.3645 1 26.3421 1 21.0005C1 15.659 3.08053 10.6355 6.85751 6.85751C10.6355 3.08053 15.6579 1 21.0005 1C26.3431 1 31.3655 3.08053 35.1425 6.85751C38.9205 10.6355 41 15.6579 41 20.9995C41 26.3421 38.9195 31.3645 35.1425 35.1414C31.3645 38.9195 26.3421 40.999 21.0005 40.999C15.6579 40.999 10.6355 38.9184 6.85856 35.1414L6.85751 35.1425ZM8.01709 8.01709C4.54884 11.4843 2.63889 16.0954 2.63889 21.0005C2.63889 25.9046 4.54884 30.5157 8.01709 33.984C11.4853 37.4522 16.0964 39.3622 21.0005 39.3622C25.9046 39.3622 30.5157 37.4522 33.984 33.984C37.4522 30.5157 39.3622 25.9046 39.3622 21.0005C39.3622 16.0964 37.4522 11.4853 33.984 8.01709C30.5157 4.54883 25.9046 2.63889 21.0005 2.63889C16.0964 2.63889 11.4853 4.54883 8.01709 8.01709Z" fill="#CDCDCD" stroke="#CDCDCD" strokeWidth="2"/>
          <path d="M27.2307 14.4875C27.761 14.4876 28.2774 14.7702 28.678 15.2991C29.0788 15.8285 29.342 16.5825 29.342 17.4358C29.342 18.2892 29.0789 19.0431 28.678 19.5725C28.2774 20.1014 27.761 20.384 27.2307 20.384C26.7003 20.384 26.1831 20.1016 25.7825 19.5725C25.3816 19.0431 25.1194 18.2891 25.1194 17.4358C25.1194 16.5825 25.3816 15.8285 25.7825 15.2991C26.1831 14.77 26.7003 14.4875 27.2307 14.4875Z" fill="#CDCDCD" stroke="#CDCDCD"/>
          <path d="M14.2327 14.4875C14.7629 14.4876 15.2794 14.7702 15.6799 15.2991C16.0808 15.8285 16.344 16.5825 16.344 17.4358C16.344 18.2892 16.0808 19.0431 15.6799 19.5725C15.2794 20.1014 14.763 20.384 14.2327 20.384C13.7022 20.384 13.1851 20.1016 12.7844 19.5725C12.3836 19.0431 12.1213 18.2891 12.1213 17.4358C12.1214 16.5825 12.3836 15.8285 12.7844 15.2991C13.1851 14.77 13.7022 14.4875 14.2327 14.4875Z" fill="#CDCDCD" stroke="#CDCDCD"/>
          <path d="M29.134 26.5166V26.5264C29.134 31.0151 25.489 34.6602 21.0002 34.6602C16.5116 34.66 12.8665 31.015 12.8665 26.5264V26.5166H29.134Z" fill="#CDCDCD" stroke="#CDCDCD"/>
        </svg>
      ),
      green: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 42 42" fill="none">
          <path d="M6.85751 35.1425C3.08053 31.3645 1 26.3421 1 21.0005C1 15.659 3.08053 10.6355 6.85751 6.85751C10.6355 3.08053 15.6579 1 21.0005 1C26.3431 1 31.3655 3.08053 35.1425 6.85751C38.9205 10.6355 41 15.6579 41 20.9995C41 26.3421 38.9195 31.3645 35.1425 35.1414C31.3645 38.9195 26.3421 40.999 21.0005 40.999C15.6579 40.999 10.6355 38.9184 6.85856 35.1414L6.85751 35.1425ZM8.01709 8.01709C4.54884 11.4843 2.63889 16.0954 2.63889 21.0005C2.63889 25.9046 4.54884 30.5157 8.01709 33.984C11.4853 37.4522 16.0964 39.3622 21.0005 39.3622C25.9046 39.3622 30.5157 37.4522 33.984 33.984C37.4522 30.5157 39.3622 25.9046 39.3622 21.0005C39.3622 16.0964 37.4522 11.4853 33.984 8.01709C30.5157 4.54883 25.9046 2.63889 21.0005 2.63889C16.0964 2.63889 11.4853 4.54883 8.01709 8.01709Z" fill="#1CA754" stroke="#1CA754" strokeWidth="2"/>
          <path d="M27.2307 14.4875C27.761 14.4876 28.2774 14.7702 28.678 15.2991C29.0788 15.8285 29.342 16.5825 29.342 17.4358C29.342 18.2892 29.0789 19.0431 28.678 19.5725C28.2774 20.1014 27.761 20.384 27.2307 20.384C26.7003 20.384 26.1831 20.1016 25.7825 19.5725C25.3816 19.0431 25.1194 18.2891 25.1194 17.4358C25.1194 16.5825 25.3816 15.8285 25.7825 15.2991C26.1831 14.77 26.7003 14.4875 27.2307 14.4875Z" fill="#1CA754" stroke="#1CA754"/>
          <path d="M14.2327 14.4875C14.7629 14.4876 15.2794 14.7702 15.6799 15.2991C16.0808 15.8285 16.344 16.5825 16.344 17.4358C16.344 18.2892 16.0808 19.0431 15.6799 19.5725C15.2794 20.1014 14.763 20.384 14.2327 20.384C13.7022 20.384 13.1851 20.1016 12.7844 19.5725C12.3836 19.0431 12.1213 18.2891 12.1213 17.4358C12.1214 16.5825 12.3836 15.8285 12.7844 15.2991C13.1851 14.77 13.7022 14.4875 14.2327 14.4875Z" fill="#1CA754" stroke="#1CA754"/>
          <path d="M29.134 26.5166V26.5264C29.134 31.0151 25.489 34.6602 21.0002 34.6602C16.5116 34.66 12.8665 31.015 12.8665 26.5264V26.5166H29.134Z" fill="#1CA754" stroke="#1CA754"/>
        </svg>
      ),
      yellow: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 42 42" fill="none">
          <path d="M6.85751 35.1425C3.08053 31.3645 1 26.3421 1 21.0005C1 15.6579 3.08053 10.6355 6.85751 6.85856C10.6355 3.08053 15.6579 1 21.0005 1C26.3431 1 31.3655 3.08053 35.1425 6.85751C38.9205 10.6355 41 15.6579 41 20.9995C41 26.341 38.9195 31.3645 35.1425 35.1414C31.3645 38.9195 26.3421 40.999 21.0005 40.999C15.6579 40.999 10.6355 38.9184 6.85856 35.1414L6.85751 35.1425ZM8.01709 8.01709C4.54884 11.4843 2.63889 16.0954 2.63889 21.0005C2.63889 25.9046 4.54884 30.5157 8.01709 33.984C11.4853 37.4522 16.0964 39.3622 21.0005 39.3622C25.9046 39.3622 30.5157 37.4522 33.984 33.984C37.4522 30.5157 39.3622 25.9046 39.3622 21.0005C39.3622 16.0964 37.4522 11.4853 33.984 8.01709C30.5157 4.54883 25.9046 2.63889 21.0005 2.63889C16.0964 2.63889 11.4853 4.54883 8.01709 8.01709Z" fill="#E5B92C" stroke="#E5B92C" strokeWidth="2"/>
          <path d="M27.2305 20.8843C28.6726 20.8843 29.8416 19.3404 29.8416 17.4359C29.8416 15.5314 28.6726 13.9875 27.2305 13.9875C25.7884 13.9875 24.6194 15.5314 24.6194 17.4359C24.6194 19.3404 25.7884 20.8843 27.2305 20.8843Z" fill="#E5B92C"/>
          <path d="M14.2325 20.8843C15.6746 20.8843 16.8436 19.3404 16.8436 17.4359C16.8436 15.5314 15.6746 13.9875 14.2325 13.9875C12.7904 13.9875 11.6213 15.5314 11.6213 17.4359C11.6213 19.3404 12.7904 20.8843 14.2325 20.8843Z" fill="#E5B92C"/>
          <path d="M13.9144 29.4734H27.2327C27.6849 29.4734 28.0522 29.106 28.0522 28.6539C28.0522 28.2018 27.6849 27.8345 27.2327 27.8345H13.9144C13.4623 27.8345 13.095 28.2018 13.095 28.6539C13.095 29.106 13.4623 29.4734 13.9144 29.4734Z" fill="#E5B92C"/>
        </svg>
      ),
      red: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 42 42" fill="none">
          <path d="M6.85751 35.1425C3.08053 31.3645 1 26.3421 1 21.0005C1 15.659 3.08053 10.6355 6.85751 6.85751C10.6355 3.08053 15.6579 1 21.0005 1C26.3431 1 31.3655 3.08053 35.1425 6.85751C38.9205 10.6355 41 15.6579 41 20.9995C41 26.3421 38.9195 31.3645 35.1425 35.1414C31.3645 38.9195 26.3421 40.999 21.0005 40.999C15.6579 40.999 10.6355 38.9184 6.85856 35.1414L6.85751 35.1425ZM8.01709 8.01709C4.54884 11.4843 2.63889 16.0954 2.63889 21.0005C2.63889 25.9057 4.54884 30.5157 8.01709 33.984C11.4853 37.4522 16.0964 39.3622 21.0005 39.3622C25.9046 39.3622 30.5157 37.4522 33.984 33.984C37.4522 30.5157 39.3622 25.9046 39.3622 21.0005C39.3622 16.0964 37.4522 11.4853 33.984 8.01709C30.5157 4.54883 25.9046 2.63889 21.0005 2.63889C16.0964 2.63889 11.4853 4.54883 8.01709 8.01709Z" fill="#C94545" stroke="#C94545" strokeWidth="2"/>
          <path d="M27.2305 20.8843C28.6726 20.8843 29.8416 19.3404 29.8416 17.4359C29.8416 15.5314 28.6726 13.9875 27.2305 13.9875C25.7884 13.9875 24.6194 15.5314 24.6194 17.4359C24.6194 19.3404 25.7884 20.8843 27.2305 20.8843Z" fill="#C94545"/>
          <path d="M14.2325 20.8843C15.6746 20.8843 16.8436 19.3404 16.8436 17.4359C16.8436 15.5314 15.6746 13.9875 14.2325 13.9875C12.7904 13.9875 11.6213 15.5314 11.6213 17.4359C11.6213 19.3404 12.7904 20.8843 14.2325 20.8843Z" fill="#C94545"/>
          <path d="M13.3922 31.1059C13.611 31.1059 13.8286 31.019 13.9908 30.8464C15.8589 28.8517 18.2178 27.8396 21.0006 27.8396C23.7834 27.8396 26.1423 28.8517 28.0104 30.8464C28.3191 31.1771 28.8382 31.1938 29.1689 30.884C29.4996 30.5743 29.5163 30.0562 29.2066 29.7255C27.0465 27.4189 24.2093 26.1997 21.0006 26.1997C17.7458 26.1997 14.9851 27.3854 12.7946 29.7255C12.4849 30.0562 12.5027 30.5743 12.8323 30.884C12.9903 31.0316 13.1913 31.1049 13.3922 31.1049V31.1059Z" fill="#C94545"/>
        </svg>
      ),
      darkRed: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 42 42" fill="none">
          <path d="M6.85751 35.1425C3.08053 31.3645 1 26.3421 1 21.0005C1 15.659 3.08053 10.6355 6.85751 6.85751C10.6355 3.08053 15.6579 1 21.0005 1C26.3431 1 31.3655 3.08053 35.1425 6.85751C38.9205 10.6355 41 15.6579 41 20.9995C41 26.341 38.9195 31.3645 35.1425 35.1414C31.3645 38.9195 26.3421 40.999 21.0005 40.999C15.6579 40.999 10.6355 38.9184 6.85856 35.1414L6.85751 35.1425ZM8.01709 8.01709C4.54884 11.4843 2.63889 16.0954 2.63889 21.0005C2.63889 25.9057 4.54884 30.5157 8.01709 33.984C11.4853 37.4522 16.0964 39.3622 21.0005 39.3622C25.9046 39.3622 30.5157 37.4522 33.984 33.984C37.4522 30.5157 39.3622 25.9046 39.3622 21.0005C39.3622 16.0964 37.4522 11.4853 33.984 8.01709C30.5157 4.54883 25.9046 2.63889 21.0005 2.63889C16.0964 2.63889 11.4853 4.54883 8.01709 8.01709Z" fill="#8E2834" stroke="#8E2834" strokeWidth="2"/>
          <path d="M28.0134 31.9536V32.367H13.9866V31.9536C13.9866 28.0803 17.1262 24.9407 20.9995 24.9407C24.8727 24.9407 28.0124 28.0803 28.0124 31.9536H28.0134Z" fill="#8E2834"/>
          <path d="M15.6139 13.6067L13.3021 15.9185L10.9903 13.6067L9.97827 14.6177L12.2901 16.9295L9.97827 19.2413L10.9903 20.2533L13.3021 17.9415L15.6139 20.2533L16.6249 19.2413L14.3141 16.9295L16.6249 14.6177L15.6139 13.6067Z" fill="#8E2834"/>
          <path d="M31.0934 13.6067L28.7816 15.9185L26.4698 13.6067L25.4578 14.6177L27.7696 16.9295L25.4578 19.2413L26.4698 20.2533L28.7816 17.9415L31.0934 20.2533L32.1054 19.2413L29.7936 16.9295L32.1054 14.6177L31.0934 13.6067Z" fill="#8E2834"/>
        </svg>
      )
    };

    if (!level) return svgs.default;
    
    if (moodType === 'happiness') { // ความสุข (อารมณ์เชิงบวก)
      if (level === 'มากที่สุด') return svgs.green;
      if (level === 'มาก') return svgs.yellow;
      if (level === 'ปานกลาง') return svgs.red;
      if (level === 'น้อย') return svgs.darkRed;
    } else { // อารมณ์เชิงลบ (เศร้า, กังวล, โกรธ, เหนื่อยล้า)
      if (level === 'มากที่สุด') return svgs.darkRed;
      if (level === 'มาก') return svgs.red;
      if (level === 'ปานกลาง') return svgs.yellow;
      if (level === 'น้อย') return svgs.green;
    }
    
    return svgs.default;
  };



  const handleItemCheck = (item) => {
    setCheckedItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };
  
  const handleSubItemCheck = (category, item, value = null) => {
    if (category === 'symptoms') {
      setSymptomSubItems(prev => ({
        ...prev,
        [item]: !prev[item]
      }));
    } else if (category === 'cvRisk') {
      setCvRiskSubItems(prev => ({
        ...prev,
        [item]: value !== null ? value : !prev[item]
      }));
    } else if (category === 'ckdRisk') {
      setCkdRiskSubItems(prev => ({
        ...prev,
        [item]: value !== null ? value : !prev[item]
      }));
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const maxImages = 3;
    const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
    
    // Check if adding these files would exceed the limit
    if (foodData.images.length + files.length > maxImages) {
      alert(`สามารถอัปโหลดรูปภาพได้สูงสุด ${maxImages} รูป`);
      return;
    }
    
    // Validate each file
    for (let file of files) {
      if (file.size > maxFileSize) {
        alert(`ไฟล์ ${file.name} มีขนาดใหญ่เกิน 10MB`);
        return;
      }
      
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        alert(`ไฟล์ ${file.name} ไม่ใช่ไฟล์ JPEG หรือ PNG`);
        return;
      }
    }
    
    // Process valid files
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFoodData(prev => ({
          ...prev,
          images: [...prev.images, {
            id: Date.now() + Math.random(),
            file,
            preview: e.target.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
    
    event.target.value = '';
  };

  const handleRemoveImage = (imageId) => {
    setFoodData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  const handleFoodDataChange = (field, value) => {
    setFoodData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      console.log('Saving daily health record...', {
        date: selectedDate,
        time: selectedTime,
        checkedItems,
        bloodPressureData,
        bloodSugarData,
        weightHeightData
      });

      // สร้าง datetime string จาก selectedDate และ selectedTime
      const datetime = `${selectedDate} ${selectedTime}:00`;

      // บันทึกความดันโลหิตและชีพจร
      if (checkedItems.bloodPressure && bloodPressureData.systolic && bloodPressureData.diastolic) {
        await addBloodPressure(
          parseFloat(bloodPressureData.systolic),
          parseFloat(bloodPressureData.diastolic),
          bloodPressureData.pulse ? parseFloat(bloodPressureData.pulse) : null,
          datetime
        );
        console.log('✅ บันทึกความดันโลหิตสำเร็จ');
        if (bloodPressureData.pulse) {
          console.log('✅ บันทึกชีพจรสำเร็จ');
        }
      }

      // บันทึกน้ำตาลในเลือด
      if (checkedItems.bloodSugar) {
        if (bloodSugarData.nonNpo) {
          await addLabData([{ dateadd: datetime, name: 'sugar_non_npo', value: bloodSugarData.nonNpo.toString(), uploader: 'm' }]);
          console.log('✅ บันทึก Non-NPO สำเร็จ');
        }
        if (bloodSugarData.fasting) {
          await addLabData([{ dateadd: datetime, name: 'sugar_fasting', value: bloodSugarData.fasting.toString(), uploader: 'm' }]);
          console.log('✅ บันทึก Fasting สำเร็จ');
        }
        if (bloodSugarData.hba1c) {
          await addLabData([{ dateadd: datetime, name: 'hba1c_daily', value: bloodSugarData.hba1c.toString(), uploader: 'm' }]);
          console.log('✅ บันทึก HbA1C สำเร็จ');
        }
      }

      // บันทึกน้ำหนักและส่วนสูง
      if (checkedItems.weightHeight) {
        if (weightHeightData.weight) {
          await addWeight(parseFloat(weightHeightData.weight), datetime);
          console.log('✅ บันทึกน้ำหนักสำเร็จ');
        }
        if (weightHeightData.height) {
          await addLabData([{ dateadd: datetime, name: 'height', value: weightHeightData.height.toString(), uploader: 'm' }]);
          console.log('✅ บันทึกส่วนสูงสำเร็จ');
        }
      }

      alert('บันทึกข้อมูลเรียบร้อยแล้ว');
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ เกิดข้อผิดพลาดในการบันทึกข้อมูล:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง');
    }
  };

  return (
    <DailyHealthRecordContainer>
      <MainHeader />

      <Content>
        <PageTitle>บันทึกประจำวัน</PageTitle>

        <BackButton onClick={() => navigate('/dashboard')}>
          ‹ ย้อนกลับ
        </BackButton>

        <InstructionSection>
          <InstructionText>
            ท่านสามารถติ๊ก <CheckMark>✓</CheckMark> ที่ช่องเพื่อกรอกข้อมูลเฉพาะที่ต้องการบันทึกผลตรวจ <span style={{ color: '#ff0000' }}>(ไม่ต้องกรอกทั้งหมด)</span>
          </InstructionText>
        </InstructionSection>

        <FormSection>
          <DateTimeSection>
            <SectionTitle>เลือกวันที่และเวลา</SectionTitle>

            <DatePickerContainer>
              <SectionTitle style={{ fontSize: '16px', marginBottom: '8px' }}>วันที่</SectionTitle>
              <DateInput
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </DatePickerContainer>

            <TimePickerContainer>
              <SectionTitle style={{ fontSize: '16px', marginBottom: '8px' }}>เวลา</SectionTitle>
              <TimeInput
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
              />
            </TimePickerContainer>
          </DateTimeSection>

          <CheckboxSection>
            <CheckboxGrid>
              <CheckboxItem>
                <Checkbox
                  id="bloodPressure"
                  checked={checkedItems.bloodPressure}
                  onChange={() => handleItemCheck('bloodPressure')}
                />
                <CheckboxLabel htmlFor="bloodPressure">ความดันโลหิต</CheckboxLabel>
              </CheckboxItem>

              {checkedItems.bloodPressure && (
                <SubCheckboxContainer $show={checkedItems.bloodPressure}>
                  <div style={{ padding: '16px 0', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <DateInput
                          type="number"
                          placeholder="ตัวบน"
                          value={bloodPressureData.systolic}
                          onChange={(e) => setBloodPressureData(prev => ({ ...prev, systolic: e.target.value }))}
                          style={{ width: '80px' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666' }}>/</span>
                        <DateInput
                          type="number"
                          placeholder="ตัวล่าง"
                          value={bloodPressureData.diastolic}
                          onChange={(e) => setBloodPressureData(prev => ({ ...prev, diastolic: e.target.value }))}
                          style={{ width: '80px' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>mmHg</span>
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DateInput
                          type="number"
                          placeholder="ชีพจร"
                          value={bloodPressureData.pulse}
                          onChange={(e) => setBloodPressureData(prev => ({ ...prev, pulse: e.target.value }))}
                          style={{ width: 'calc(100% - 120px)' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif", whiteSpace: 'nowrap' }}>ครั้งต่อนาที</span>
                      </div>
                    </div>
                    
                  </div>
                </SubCheckboxContainer>
              )}

              <CheckboxItem>
                <Checkbox
                  id="bloodSugar"
                  checked={checkedItems.bloodSugar}
                  onChange={() => handleItemCheck('bloodSugar')}
                />
                <CheckboxLabel htmlFor="bloodSugar">น้ำตาลในเลือด</CheckboxLabel>
              </CheckboxItem>

              {checkedItems.bloodSugar && (
                <SubCheckboxContainer $show={checkedItems.bloodSugar}>
                  <div style={{ padding: '16px 0', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>น้ำตาลในเลือด (Non-NPO blood glucose)</SubCheckboxLabel>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DateInput
                          type="number"
                          step="0.1"
                          placeholder="Non-NPO"
                          value={bloodSugarData.nonNpo}
                          onChange={(e) => setBloodSugarData(prev => ({ ...prev, nonNpo: e.target.value }))}
                          style={{ width: 'calc(100% - 100px)' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif", whiteSpace: 'nowrap' }}>mg/dL</span>
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>น้ำตาลอดอาหาร (Fasting blood glucose)</SubCheckboxLabel>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DateInput
                          type="number"
                          step="0.1"
                          placeholder="Fasting"
                          value={bloodSugarData.fasting}
                          onChange={(e) => setBloodSugarData(prev => ({ ...prev, fasting: e.target.value }))}
                          style={{ width: 'calc(100% - 100px)' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif", whiteSpace: 'nowrap' }}>mg/dL</span>
                      </div>
                    </div>

                    <div>
                      <SubCheckboxLabel style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>HbA1C</SubCheckboxLabel>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DateInput
                          type="number"
                          step="0.1"
                          placeholder="HbA1C"
                          value={bloodSugarData.hba1c}
                          onChange={(e) => setBloodSugarData(prev => ({ ...prev, hba1c: e.target.value }))}
                          style={{ width: 'calc(100% - 100px)' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif", whiteSpace: 'nowrap' }}>%</span>
                      </div>
                    </div>
                  </div>
                </SubCheckboxContainer>
              )}

              <CheckboxItem>
                <Checkbox
                  id="weightHeight"
                  checked={checkedItems.weightHeight}
                  onChange={() => handleItemCheck('weightHeight')}
                />
                <CheckboxLabel htmlFor="weightHeight">น้ำหนักและส่วนสูง</CheckboxLabel>
              </CheckboxItem>

              {checkedItems.weightHeight && (
                <SubCheckboxContainer $show={checkedItems.weightHeight}>
                  <div style={{ padding: '16px 0', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DateInput
                          type="number"
                          placeholder="น้ำหนัก"
                          value={weightHeightData.weight}
                          onChange={(e) => setWeightHeightData(prev => ({ ...prev, weight: e.target.value }))}
                          style={{ width: 'calc(50% - 60px)' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif", whiteSpace: 'nowrap' }}>กิโลกรัม</span>
                        <DateInput
                          type="number"
                          placeholder="ส่วนสูง"
                          value={weightHeightData.height}
                          onChange={(e) => setWeightHeightData(prev => ({ ...prev, height: e.target.value }))}
                          style={{ width: 'calc(50% - 80px)' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif", whiteSpace: 'nowrap' }}>เซนติเมตร</span>
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>รอบเอว</SubCheckboxLabel>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DateInput
                          type="number"
                          placeholder="รอบเอว"
                          value={weightHeightData.waist}
                          onChange={(e) => setWeightHeightData(prev => ({ ...prev, waist: e.target.value }))}
                          style={{ width: 'calc(100% - 60px)' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif", whiteSpace: 'nowrap' }}>นิ้ว</span>
                      </div>
                    </div>
                    
                    <div>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>ไขมัน (ถ้าทราบ % ไขมัน)</SubCheckboxLabel>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DateInput
                          type="number"
                          placeholder="ไขมัน"
                          value={weightHeightData.bodyFat}
                          onChange={(e) => setWeightHeightData(prev => ({ ...prev, bodyFat: e.target.value }))}
                          style={{ width: 'calc(100% - 40px)' }}
                        />
                        <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif", whiteSpace: 'nowrap' }}>%</span>
                      </div>
                    </div>
                  </div>
                </SubCheckboxContainer>
              )}

              {/* <CheckboxItem>
                <Checkbox
                  id="food"
                  checked={checkedItems.food}
                  onChange={() => handleItemCheck('food')}
                />
                <CheckboxLabel htmlFor="food">อาหาร</CheckboxLabel>
              </CheckboxItem> */}

              {checkedItems.food && (
                <SubCheckboxContainer $show={checkedItems.food}>
                  <div style={{ padding: '16px 16px', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    {/* อัปโหลดรูปภาพอาหาร */}
                    <ImageUploadContainer>
                      <FoodFormLabel>รูปภาพอาหาร</FoodFormLabel>
                      <ImageUploadArea>
                        <UploadIcon>
                          <svg xmlns="http://www.w3.org/2000/svg" width="47" height="47" viewBox="0 0 47 47" fill="none">
                            <path d="M14.7988 37.4118H11.1188C5.48376 37.0093 2.95386 32.6777 2.95386 28.8252C2.95386 24.9727 5.48378 20.6219 11.0229 20.2385C11.8088 20.1619 12.4988 20.7752 12.5563 21.5802C12.6138 22.366 12.0197 23.056 11.2147 23.1135C7.49635 23.3819 5.82886 26.1802 5.82886 28.8443C5.82886 31.5085 7.49635 34.3069 11.2147 34.5752H14.7988C15.5847 34.5752 16.2363 35.2269 16.2363 36.0127C16.2363 36.7985 15.5847 37.4118 14.7988 37.4118Z" fill="#292D32"/>
                            <path d="M32.4507 37.4119C32.4124 37.4119 32.3932 37.4119 32.3549 37.4119C31.5691 37.4119 30.8408 36.7602 30.8408 35.9744C30.8408 35.1502 31.4541 34.5369 32.2591 34.5369C34.6166 34.5369 36.7249 33.7127 38.3733 32.2369C41.3633 29.6302 41.5549 25.8736 40.7499 23.2286C39.9449 20.6027 37.7024 17.5935 33.7924 17.1144C33.1599 17.0377 32.6615 16.5586 32.5465 15.9261C31.7799 11.3261 29.3074 8.14439 25.5508 6.99439C21.6791 5.78689 17.1557 6.97522 14.3382 9.92688C11.5974 12.7827 10.9649 16.7886 12.5557 21.1969C12.824 21.9444 12.4408 22.7685 11.6933 23.0369C10.9458 23.3052 10.1216 22.9219 9.85326 22.1744C7.91743 16.7694 8.79911 11.5944 12.2683 7.95272C15.8141 4.23439 21.5066 2.75854 26.3941 4.25354C30.8791 5.63354 34.0415 9.33271 35.1915 14.4694C39.1015 15.351 42.2449 18.3219 43.4907 22.4236C44.8516 26.8894 43.6249 31.4894 40.2707 34.4027C38.1432 36.3194 35.364 37.4119 32.4507 37.4119Z" fill="#292D32"/>
                            <path d="M23.5001 43.0469C19.6476 43.0469 16.0443 40.9961 14.0701 37.6802C13.8593 37.3544 13.6485 36.9711 13.476 36.5494C12.8243 35.1886 12.4792 33.6361 12.4792 32.0261C12.4792 25.9502 17.4242 21.0052 23.5001 21.0052C29.5759 21.0052 34.5209 25.9502 34.5209 32.0261C34.5209 33.6552 34.176 35.1886 33.486 36.6069C33.3326 36.9711 33.1218 37.3544 32.8918 37.7186C30.956 40.9961 27.3526 43.0469 23.5001 43.0469ZM23.5001 23.8802C19.0151 23.8802 15.3542 27.5411 15.3542 32.0261C15.3542 33.2144 15.6034 34.3261 16.0826 35.3419C16.2359 35.6677 16.3701 35.9361 16.5234 36.1852C17.9801 38.6577 20.6442 40.1719 23.4809 40.1719C26.3176 40.1719 28.9817 38.6578 30.4192 36.2236C30.5917 35.9361 30.7451 35.6677 30.8601 35.3994C31.3776 34.3452 31.6267 33.2336 31.6267 32.0452C31.6459 27.5411 27.9851 23.8802 23.5001 23.8802Z" fill="#292D32"/>
                            <path d="M22.4074 35.3609C22.0432 35.3609 21.6791 35.2267 21.3916 34.9392L19.494 33.0417C18.9382 32.4859 18.9382 31.5659 19.494 31.0101C20.0499 30.4542 20.9699 30.4542 21.5257 31.0101L22.4458 31.9301L25.5124 29.0934C26.1066 28.5567 27.0074 28.5951 27.544 29.1701C28.0807 29.7451 28.0424 30.6651 27.4674 31.2018L23.3849 34.9776C23.0974 35.2268 22.7524 35.3609 22.4074 35.3609Z" fill="#292D32"/>
                          </svg>
                        </UploadIcon>
                        <UploadText>
                          ส่งรูปอาหาร JPEG, PNG, up to 10MB
                        </UploadText>
                        <label>
                          <BrowseButton as="span">
                            Browse File
                          </BrowseButton>
                          <HiddenFileInput
                            type="file"
                            accept=".jpeg,.jpg,.png"
                            onChange={handleImageUpload}
                            multiple
                          />
                        </label>
                      </ImageUploadArea>
                      
                      {foodData.images.length > 0 && (
                        <ImagePreviewGrid>
                          {foodData.images.map((image) => (
                            <ImagePreviewItem key={image.id}>
                              <ImagePreview src={image.preview} alt="อาหาร" />
                              <ImageRemoveButton onClick={() => handleRemoveImage(image.id)}>
                                ×
                              </ImageRemoveButton>
                            </ImagePreviewItem>
                          ))}
                        </ImagePreviewGrid>
                      )}
                    </ImageUploadContainer>

                    {/* ชื่ออาหาร */}
                    <FoodFormSection>
                      <FoodFormLabel>ชื่ออาหาร</FoodFormLabel>
                      <FoodInput
                        type="text"
                        placeholder="กรอกชื่ออาหาร"
                        value={foodData.name}
                        onChange={(e) => handleFoodDataChange('name', e.target.value)}
                      />
                    </FoodFormSection>

                    {/* คำอธิบายอาหาร */}
                    <FoodFormSection>
                      <FoodFormLabel>คำอธิบายอาหาร</FoodFormLabel>
                      <FoodTextarea
                        placeholder="อธิบายรายละเอียดอาหารโดยละเอียด เช่น ข้าวสวย 1 ถ้วย ทุเรียน 1 พู"
                        value={foodData.description}
                        onChange={(e) => handleFoodDataChange('description', e.target.value)}
                      />
                    </FoodFormSection>

                    {/* ประเภทมื้อ */}
                    <FoodFormSection>
                      <FoodFormLabel>ประเภทมื้อ</FoodFormLabel>
                      <FoodSelect
                        value={foodData.mealType}
                        onChange={(e) => handleFoodDataChange('mealType', e.target.value)}
                      >
                        <option value="มื้อหลัก">มื้อหลัก</option>
                        <option value="อาหารว่าง">อาหารว่าง</option>
                      </FoodSelect>
                    </FoodFormSection>

                    {/* นับคาร์บ */}
                    <FoodFormSection>
                      <FoodFormLabel>นับคาร์บ (ถ้าทราบ)</FoodFormLabel>
                      <FoodInput
                        type="number"
                        placeholder="กรอกจำนวนคาร์บ"
                        value={foodData.carbs}
                        onChange={(e) => handleFoodDataChange('carbs', e.target.value)}
                      />
                    </FoodFormSection>

                    {/* ระดับความหวาน */}
                    <FoodFormSection>
                      <FoodFormLabel>ระดับความหวาน</FoodFormLabel>
                      <RadioGrid>
                        <RadioLabel>
                          <RadioInput
                            name="sweetness"
                            value="ไม่หวาน"
                            checked={foodData.sweetnessLevel === 'ไม่หวาน'}
                            onChange={(e) => handleFoodDataChange('sweetnessLevel', e.target.value)}
                          />
                          ไม่หวาน
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="sweetness"
                            value="หวานน้อย"
                            checked={foodData.sweetnessLevel === 'หวานน้อย'}
                            onChange={(e) => handleFoodDataChange('sweetnessLevel', e.target.value)}
                          />
                          หวานน้อย
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="sweetness"
                            value="หวานปกติ"
                            checked={foodData.sweetnessLevel === 'หวานปกติ'}
                            onChange={(e) => handleFoodDataChange('sweetnessLevel', e.target.value)}
                          />
                          หวานปกติ
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="sweetness"
                            value="หวานมาก"
                            checked={foodData.sweetnessLevel === 'หวานมาก'}
                            onChange={(e) => handleFoodDataChange('sweetnessLevel', e.target.value)}
                          />
                          หวานมาก
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="sweetness"
                            value="หวานมากที่สุด"
                            checked={foodData.sweetnessLevel === 'หวานมากที่สุด'}
                            onChange={(e) => handleFoodDataChange('sweetnessLevel', e.target.value)}
                          />
                          หวานมากที่สุด
                        </RadioLabel>
                      </RadioGrid>
                    </FoodFormSection>

                    {/* ระดับความเค็ม */}
                    <FoodFormSection>
                      <FoodFormLabel>ระดับความเค็ม</FoodFormLabel>
                      <RadioGrid>
                        <RadioLabel>
                          <RadioInput
                            name="saltiness"
                            value="ไม่เค็ม"
                            checked={foodData.saltinessLevel === 'ไม่เค็ม'}
                            onChange={(e) => handleFoodDataChange('saltinessLevel', e.target.value)}
                          />
                          ไม่เค็ม
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="saltiness"
                            value="เค็มน้อย"
                            checked={foodData.saltinessLevel === 'เค็มน้อย'}
                            onChange={(e) => handleFoodDataChange('saltinessLevel', e.target.value)}
                          />
                          เค็มน้อย
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="saltiness"
                            value="เค็มปกติ"
                            checked={foodData.saltinessLevel === 'เค็มปกติ'}
                            onChange={(e) => handleFoodDataChange('saltinessLevel', e.target.value)}
                          />
                          เค็มปกติ
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="saltiness"
                            value="เค็มมาก"
                            checked={foodData.saltinessLevel === 'เค็มมาก'}
                            onChange={(e) => handleFoodDataChange('saltinessLevel', e.target.value)}
                          />
                          เค็มมาก
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="saltiness"
                            value="เค็มมากที่สุด"
                            checked={foodData.saltinessLevel === 'เค็มมากที่สุด'}
                            onChange={(e) => handleFoodDataChange('saltinessLevel', e.target.value)}
                          />
                          เค็มมากที่สุด
                        </RadioLabel>
                      </RadioGrid>
                    </FoodFormSection>

                    {/* ระดับความมัน */}
                    <FoodFormSection>
                      <FoodFormLabel>ระดับความมัน</FoodFormLabel>
                      <RadioGrid>
                        <RadioLabel>
                          <RadioInput
                            name="oiliness"
                            value="ไม่มัน"
                            checked={foodData.oilinessLevel === 'ไม่มัน'}
                            onChange={(e) => handleFoodDataChange('oilinessLevel', e.target.value)}
                          />
                          ไม่มัน
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="oiliness"
                            value="มันน้อย"
                            checked={foodData.oilinessLevel === 'มันน้อย'}
                            onChange={(e) => handleFoodDataChange('oilinessLevel', e.target.value)}
                          />
                          มันน้อย
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="oiliness"
                            value="ปกติ"
                            checked={foodData.oilinessLevel === 'ปกติ'}
                            onChange={(e) => handleFoodDataChange('oilinessLevel', e.target.value)}
                          />
                          ปกติ
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="oiliness"
                            value="มันมาก"
                            checked={foodData.oilinessLevel === 'มันมาก'}
                            onChange={(e) => handleFoodDataChange('oilinessLevel', e.target.value)}
                          />
                          มันมาก
                        </RadioLabel>
                        <RadioLabel>
                          <RadioInput
                            name="oiliness"
                            value="มันมากที่สุด"
                            checked={foodData.oilinessLevel === 'มันมากที่สุด'}
                            onChange={(e) => handleFoodDataChange('oilinessLevel', e.target.value)}
                          />
                          มันมากที่สุด
                        </RadioLabel>
                      </RadioGrid>
                    </FoodFormSection>
                  </div>
                </SubCheckboxContainer>
              )}

              {/* <CheckboxItem>
                <Checkbox
                  id="mood"
                  checked={checkedItems.mood}
                  onChange={() => handleItemCheck('mood')}
                />
                <CheckboxLabel htmlFor="mood">อารมณ์</CheckboxLabel>
              </CheckboxItem> */}

              {checkedItems.mood && (
                <SubCheckboxContainer $show={checkedItems.mood}>
                  <div style={{ padding: '16px 0', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    
                    <div style={{ marginBottom: '24px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '12px', display: 'block', color: '#00BBD3', fontWeight: 'bold' }}>
                        คุณรู้สึกอย่างไร <span style={{ color: '#666666' }}>(ตอบได้หลายข้อ)</span>
                      </SubCheckboxLabel>
                      
                      {/* ความสุข */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <div style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%', 
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {getMoodSVG('happiness', moodData.happiness)}
                          </div>
                          <span style={{ fontSize: '16px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                            ความสุข (เช่น มีความสุข, สงบสุข, ภูมิใจ)
                          </span>
                        </div>
                        <select
                          value={moodData.happiness}
                          onChange={(e) => setMoodData(prev => ({ ...prev, happiness: e.target.value }))}
                          style={{
                            width: '180px',
                            padding: '8px 12px',
                            border: '2px solid #e1e5e9',
                            borderRadius: '20px',
                            fontSize: '14px',
                            color: '#666666',
                            backgroundColor: 'white',
                            fontFamily: "'Noto Sans Thai', sans-serif",
                            marginLeft: '44px'
                          }}
                        >
                          <option value="">กรุณาเลือก</option>
                          <option value="มากที่สุด">มากที่สุด</option>
                          <option value="มาก">มาก</option>
                          <option value="ปานกลาง">ปานกลาง</option>
                          <option value="น้อย">น้อย</option>
                        </select>
                      </div>

                      {/* ความเศร้า */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <div style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%', 
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {getMoodSVG('sadness', moodData.sadness)}
                          </div>
                          <span style={{ fontSize: '16px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                            ความเศร้า (เช่น เศร้า, หดหู่, เหงา)
                          </span>
                        </div>
                        <select
                          value={moodData.sadness}
                          onChange={(e) => setMoodData(prev => ({ ...prev, sadness: e.target.value }))}
                          style={{
                            width: '180px',
                            padding: '8px 12px',
                            border: '2px solid #e1e5e9',
                            borderRadius: '20px',
                            fontSize: '14px',
                            color: '#666666',
                            backgroundColor: 'white',
                            fontFamily: "'Noto Sans Thai', sans-serif",
                            marginLeft: '44px'
                          }}
                        >
                          <option value="">กรุณาเลือก</option>
                          <option value="มากที่สุด">มากที่สุด</option>
                          <option value="มาก">มาก</option>
                          <option value="ปานกลาง">ปานกลาง</option>
                          <option value="น้อย">น้อย</option>
                        </select>
                      </div>

                      {/* ความกังวล */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <div style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%', 
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {getMoodSVG('anxiety', moodData.anxiety)}
                          </div>
                          <span style={{ fontSize: '16px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                            ความกังวล (เช่น กังวล, ตื่นเต้น, ประหม่า)
                          </span>
                        </div>
                        <select
                          value={moodData.anxiety}
                          onChange={(e) => setMoodData(prev => ({ ...prev, anxiety: e.target.value }))}
                          style={{
                            width: '180px',
                            padding: '8px 12px',
                            border: '2px solid #e1e5e9',
                            borderRadius: '20px',
                            fontSize: '14px',
                            color: '#666666',
                            backgroundColor: 'white',
                            fontFamily: "'Noto Sans Thai', sans-serif",
                            marginLeft: '44px'
                          }}
                        >
                          <option value="">กรุณาเลือก</option>
                          <option value="มากที่สุด">มากที่สุด</option>
                          <option value="มาก">มาก</option>
                          <option value="ปานกลาง">ปานกลาง</option>
                          <option value="น้อย">น้อย</option>
                        </select>
                      </div>

                      {/* ความโกรธ */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <div style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%', 
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {getMoodSVG('anger', moodData.anger)}
                          </div>
                          <span style={{ fontSize: '16px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                            ความโกรธ (เช่น โกรธ, หงุดหงิด, โมโห)
                          </span>
                        </div>
                        <select
                          value={moodData.anger}
                          onChange={(e) => setMoodData(prev => ({ ...prev, anger: e.target.value }))}
                          style={{
                            width: '180px',
                            padding: '8px 12px',
                            border: '2px solid #e1e5e9',
                            borderRadius: '20px',
                            fontSize: '14px',
                            color: '#666666',
                            backgroundColor: 'white',
                            fontFamily: "'Noto Sans Thai', sans-serif",
                            marginLeft: '44px'
                          }}
                        >
                          <option value="">กรุณาเลือก</option>
                          <option value="มากที่สุด">มากที่สุด</option>
                          <option value="มาก">มาก</option>
                          <option value="ปานกลาง">ปานกลาง</option>
                          <option value="น้อย">น้อย</option>
                        </select>
                      </div>

                      {/* ความเหนื่อยล้า */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <div style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%', 
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {getMoodSVG('fatigue', moodData.fatigue)}
                          </div>
                          <span style={{ fontSize: '16px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                            ความเหนื่อยล้า (เช่น เหนื่อย, เพลีย, หมดแรง)
                          </span>
                        </div>
                        <select
                          value={moodData.fatigue}
                          onChange={(e) => setMoodData(prev => ({ ...prev, fatigue: e.target.value }))}
                          style={{
                            width: '180px',
                            padding: '8px 12px',
                            border: '2px solid #e1e5e9',
                            borderRadius: '20px',
                            fontSize: '14px',
                            color: '#666666',
                            backgroundColor: 'white',
                            fontFamily: "'Noto Sans Thai', sans-serif",
                            marginLeft: '44px'
                          }}
                        >
                          <option value="">กรุณาเลือก</option>
                          <option value="มากที่สุด">มากที่สุด</option>
                          <option value="มาก">มาก</option>
                          <option value="ปานกลาง">ปานกลาง</option>
                          <option value="น้อย">น้อย</option>
                        </select>
                      </div>

                      {/* อื่น ๆ (โปรดระบุ) */}
                      <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <div style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%', 
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div style={{ width: '24px', height: '24px' }}></div>
                          </div>
                          <span style={{ fontSize: '16px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                            อื่น ๆ (โปรดระบุ)
                          </span>
                        </div>
                        <input
                          type="text"
                          value={moodData.other}
                          onChange={(e) => setMoodData(prev => ({ ...prev, other: e.target.value }))}
                          style={{
                            width: '350px',
                            height: '69px',
                            padding: '8px 12px',
                            border: '2px solid #e1e5e9',
                            borderRadius: '22px',
                            fontSize: '14px',
                            color: '#666666',
                            backgroundColor: 'white',
                            fontFamily: "'Noto Sans Thai', sans-serif",
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* ปัจจัยกระตุ้นอารมณ์ */}
                      <div style={{ marginBottom: '24px' }}>
                        <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '12px', display: 'block', color: '#00BBD3', fontWeight: 'bold' }}>
                          ปัจจัยกระตุ้นอารมณ์ <span style={{ color: '#666666' }}>(เลือกได้มากกว่า 1 ข้อ)</span>
                        </SubCheckboxLabel>
                        
                        {/* สถานการณ์ที่เกิดขึ้น */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              checked={moodData.moodFactors.situation}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactors: { ...prev.moodFactors, situation: e.target.checked }
                              }))}
                              style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #00BBD3',
                                borderRadius: '4px',
                                backgroundColor: moodData.moodFactors.situation ? '#00BBD3' : 'white',
                                cursor: 'pointer',
                                appearance: 'none',
                                backgroundImage: moodData.moodFactors.situation ? 
                                  "url(\"data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e\")" : 
                                  'none',
                                backgroundSize: '12px',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                              }}
                            />
                            <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                              สถานการณ์ที่เกิดขึ้น
                            </span>
                          </div>
                          {moodData.moodFactors.situation && (
                            <input
                              type="text"
                              value={moodData.moodFactorsInput.situation}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactorsInput: { ...prev.moodFactorsInput, situation: e.target.value }
                              }))}
                              style={{
                                width: '350px',
                                height: '69px',
                                padding: '8px 12px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '22px',
                                fontSize: '14px',
                                color: '#666666',
                                backgroundColor: 'white',
                                fontFamily: "'Noto Sans Thai', sans-serif",
                                boxSizing: 'border-box'
                              }}
                            />
                          )}
                        </div>

                        {/* การเปลี่ยนแปลงทางสุขภาพ */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              checked={moodData.moodFactors.healthChange}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactors: { ...prev.moodFactors, healthChange: e.target.checked }
                              }))}
                              style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #00BBD3',
                                borderRadius: '4px',
                                backgroundColor: moodData.moodFactors.healthChange ? '#00BBD3' : 'white',
                                cursor: 'pointer',
                                appearance: 'none',
                                backgroundImage: moodData.moodFactors.healthChange ? 
                                  "url(\"data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e\")" : 
                                  'none',
                                backgroundSize: '12px',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                              }}
                            />
                            <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                              การเปลี่ยนแปลงทางสุขภาพ
                            </span>
                          </div>
                          {moodData.moodFactors.healthChange && (
                            <input
                              type="text"
                              value={moodData.moodFactorsInput.healthChange}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactorsInput: { ...prev.moodFactorsInput, healthChange: e.target.value }
                              }))}
                              style={{
                                width: '350px',
                                height: '69px',
                                padding: '8px 12px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '22px',
                                fontSize: '14px',
                                color: '#666666',
                                backgroundColor: 'white',
                                fontFamily: "'Noto Sans Thai', sans-serif",
                                boxSizing: 'border-box'
                              }}
                            />
                          )}
                        </div>

                        {/* ปฏิสัมพันธ์กับผู้อื่น */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              checked={moodData.moodFactors.socialInteraction}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactors: { ...prev.moodFactors, socialInteraction: e.target.checked }
                              }))}
                              style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #00BBD3',
                                borderRadius: '4px',
                                backgroundColor: moodData.moodFactors.socialInteraction ? '#00BBD3' : 'white',
                                cursor: 'pointer',
                                appearance: 'none',
                                backgroundImage: moodData.moodFactors.socialInteraction ? 
                                  "url(\"data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e\")" : 
                                  'none',
                                backgroundSize: '12px',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                              }}
                            />
                            <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                              ปฏิสัมพันธ์กับผู้อื่น
                            </span>
                          </div>
                          {moodData.moodFactors.socialInteraction && (
                            <input
                              type="text"
                              value={moodData.moodFactorsInput.socialInteraction}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactorsInput: { ...prev.moodFactorsInput, socialInteraction: e.target.value }
                              }))}
                              style={{
                                width: '350px',
                                height: '69px',
                                padding: '8px 12px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '22px',
                                fontSize: '14px',
                                color: '#666666',
                                backgroundColor: 'white',
                                fontFamily: "'Noto Sans Thai', sans-serif",
                                boxSizing: 'border-box'
                              }}
                            />
                          )}
                        </div>

                        {/* สภาพแวดล้อม */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              checked={moodData.moodFactors.environment}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactors: { ...prev.moodFactors, environment: e.target.checked }
                              }))}
                              style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #00BBD3',
                                borderRadius: '4px',
                                backgroundColor: moodData.moodFactors.environment ? '#00BBD3' : 'white',
                                cursor: 'pointer',
                                appearance: 'none',
                                backgroundImage: moodData.moodFactors.environment ? 
                                  "url(\"data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e\")" : 
                                  'none',
                                backgroundSize: '12px',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                              }}
                            />
                            <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                              สภาพแวดล้อม
                            </span>
                          </div>
                          {moodData.moodFactors.environment && (
                            <input
                              type="text"
                              value={moodData.moodFactorsInput.environment}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactorsInput: { ...prev.moodFactorsInput, environment: e.target.value }
                              }))}
                              style={{
                                width: '350px',
                                height: '69px',
                                padding: '8px 12px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '22px',
                                fontSize: '14px',
                                color: '#666666',
                                backgroundColor: 'white',
                                fontFamily: "'Noto Sans Thai', sans-serif",
                                boxSizing: 'border-box'
                              }}
                            />
                          )}
                        </div>

                        {/* อื่นๆ (โปรดระบุ) */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              checked={moodData.moodFactors.otherFactor}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactors: { ...prev.moodFactors, otherFactor: e.target.checked }
                              }))}
                              style={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #00BBD3',
                                borderRadius: '4px',
                                backgroundColor: moodData.moodFactors.otherFactor ? '#00BBD3' : 'white',
                                cursor: 'pointer',
                                appearance: 'none',
                                backgroundImage: moodData.moodFactors.otherFactor ? 
                                  "url(\"data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e\")" : 
                                  'none',
                                backgroundSize: '12px',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                              }}
                            />
                            <span style={{ fontSize: '14px', color: '#666666', fontFamily: "'Noto Sans Thai', sans-serif" }}>
                              อื่นๆ (โปรดระบุ):
                            </span>
                          </div>
                          {moodData.moodFactors.otherFactor && (
                            <input
                              type="text"
                              value={moodData.moodFactorsInput.otherFactor}
                              onChange={(e) => setMoodData(prev => ({ 
                                ...prev, 
                                moodFactorsInput: { ...prev.moodFactorsInput, otherFactor: e.target.value }
                              }))}
                              style={{
                                width: '350px',
                                height: '69px',
                                padding: '8px 12px',
                                border: '2px solid #e1e5e9',
                                borderRadius: '22px',
                                fontSize: '14px',
                                color: '#666666',
                                backgroundColor: 'white',
                                fontFamily: "'Noto Sans Thai', sans-serif",
                                boxSizing: 'border-box'
                              }}
                            />
                          )}
                        </div>
                      </div>

                      {/* อธิบายผลกระทบที่มีต่ออารมณ์คุณ */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ fontSize: '16px', color: '#00BBD3', fontFamily: "'Noto Sans Thai', sans-serif", fontWeight: 'bold' }}>
                            อธิบายผลกระทบที่มีต่ออารมณ์คุณ
                          </span>
                        </div>
                        <textarea
                          placeholder="อธิบายผลกระทบที่มีต่ออารมณ์คุณ"
                          value={moodData.moodImpactDescription}
                          onChange={(e) => setMoodData(prev => ({ ...prev, moodImpactDescription: e.target.value }))}
                          style={{
                            width: '350px',
                            height: '69px',
                            padding: '8px 12px',
                            border: '2px solid #e1e5e9',
                            borderRadius: '22px',
                            fontSize: '14px',
                            color: '#666666',
                            backgroundColor: 'white',
                            fontFamily: "'Noto Sans Thai', sans-serif",
                            boxSizing: 'border-box'
                          }}
                        />
                      </div> 

                    </div>
                  </div>
                </SubCheckboxContainer>
              )}
              {/* <CheckboxItem>
                <Checkbox
                  id="symptoms"
                  checked={checkedItems.symptoms}
                  onChange={() => handleItemCheck('symptoms')}
                />
                <CheckboxLabel htmlFor="symptoms">อาการผิดปกติ <span style={{ color: '#00BBD3' }}>(ตอบได้หลายข้อ)</span></CheckboxLabel>
              </CheckboxItem> */}
              
              {checkedItems.symptoms && (
                <SubCheckboxContainer $show={checkedItems.symptoms}>
                  <div style={{ padding: '16px 0', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                      {/* แถว 1 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="blackouts"
                          checked={symptomSubItems.blackouts}
                          onChange={() => handleSubItemCheck('symptoms', 'blackouts')}
                        />
                        <SubCheckboxLabel htmlFor="blackouts">หน้ามืด</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="frequentUrination"
                          checked={symptomSubItems.frequentUrination}
                          onChange={() => handleSubItemCheck('symptoms', 'frequentUrination')}
                        />
                        <SubCheckboxLabel htmlFor="frequentUrination">ปัสสาวะบ่อย</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 2 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="dizzy"
                          checked={symptomSubItems.dizzy}
                          onChange={() => handleSubItemCheck('symptoms', 'dizzy')}
                        />
                        <SubCheckboxLabel htmlFor="dizzy">วิงเวียน</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="excessiveThirst"
                          checked={symptomSubItems.excessiveThirst}
                          onChange={() => handleSubItemCheck('symptoms', 'excessiveThirst')}
                        />
                        <SubCheckboxLabel htmlFor="excessiveThirst">กระหายน้ำมาก</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 3 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="headache"
                          checked={symptomSubItems.headache}
                          onChange={() => handleSubItemCheck('symptoms', 'headache')}
                        />
                        <SubCheckboxLabel htmlFor="headache">ปวดศีรษะ</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="backPain"
                          checked={symptomSubItems.backPain}
                          onChange={() => handleSubItemCheck('symptoms', 'backPain')}
                        />
                        <SubCheckboxLabel htmlFor="backPain">ปวดหลัง</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 4 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="chestTightness"
                          checked={symptomSubItems.chestTightness}
                          onChange={() => handleSubItemCheck('symptoms', 'chestTightness')}
                        />
                        <SubCheckboxLabel htmlFor="chestTightness">เจ็บแน่นหน้าอก</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="littleUrine"
                          checked={symptomSubItems.littleUrine}
                          onChange={() => handleSubItemCheck('symptoms', 'littleUrine')}
                        />
                        <SubCheckboxLabel htmlFor="littleUrine">ปัสสาวะน้อย</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 5 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="legSwelling"
                          checked={symptomSubItems.legSwelling}
                          onChange={() => handleSubItemCheck('symptoms', 'legSwelling')}
                        />
                        <SubCheckboxLabel htmlFor="legSwelling">ขา/เท้าบวม</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="foamyUrine"
                          checked={symptomSubItems.foamyUrine}
                          onChange={() => handleSubItemCheck('symptoms', 'foamyUrine')}
                        />
                        <SubCheckboxLabel htmlFor="foamyUrine">ปัสสาวะเป็นฟอง</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 6 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="decreasedUrination"
                          checked={symptomSubItems.decreasedUrination}
                          onChange={() => handleSubItemCheck('symptoms', 'decreasedUrination')}
                        />
                        <SubCheckboxLabel htmlFor="decreasedUrination">ปัสสาวะออกลดลง</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="chestPain"
                          checked={symptomSubItems.chestPain}
                          onChange={() => handleSubItemCheck('symptoms', 'chestPain')}
                        />
                        <SubCheckboxLabel htmlFor="chestPain">ปวดแน่นหน้าอก</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 7 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="palpitations"
                          checked={symptomSubItems.palpitations}
                          onChange={() => handleSubItemCheck('symptoms', 'palpitations')}
                        />
                        <SubCheckboxLabel htmlFor="palpitations">ใจสั่น</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="breathingDifficulty"
                          checked={symptomSubItems.breathingDifficulty}
                          onChange={() => handleSubItemCheck('symptoms', 'breathingDifficulty')}
                        />
                        <SubCheckboxLabel htmlFor="breathingDifficulty">หายใจลำบาก</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 8 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="handShaking"
                          checked={symptomSubItems.handShaking}
                          onChange={() => handleSubItemCheck('symptoms', 'handShaking')}
                        />
                        <SubCheckboxLabel htmlFor="handShaking">มือสั่น</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="easyFatigue"
                          checked={symptomSubItems.easyFatigue}
                          onChange={() => handleSubItemCheck('symptoms', 'easyFatigue')}
                        />
                        <SubCheckboxLabel htmlFor="easyFatigue">เหนื่อยง่าย</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 9 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="sweating"
                          checked={symptomSubItems.sweating}
                          onChange={() => handleSubItemCheck('symptoms', 'sweating')}
                        />
                        <SubCheckboxLabel htmlFor="sweating">เหงื่อออก</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="others"
                          checked={symptomSubItems.others}
                          onChange={() => handleSubItemCheck('symptoms', 'others')}
                        />
                        <SubCheckboxLabel htmlFor="others">อื่นๆ</SubCheckboxLabel>
                      </SubCheckboxItem>
                      
                      {/* แถว 10 */}
                      <SubCheckboxItem>
                        <SubCheckbox
                          id="extremeHunger"
                          checked={symptomSubItems.extremeHunger}
                          onChange={() => handleSubItemCheck('symptoms', 'extremeHunger')}
                        />
                        <SubCheckboxLabel htmlFor="extremeHunger">หิวแบบโหย</SubCheckboxLabel>
                      </SubCheckboxItem>
                      <SubCheckboxItem>
                        {symptomSubItems.others && (
                          <DateInput
                            type="text"
                            placeholder="ระบุอาการอื่นๆ"
                            value={symptomSubItems.othersText}
                            onChange={(e) => setSymptomSubItems(prev => ({ ...prev, othersText: e.target.value }))}
                            style={{ width: '100%' }}
                          />
                        )}
                      </SubCheckboxItem>
                    </div>
                  </div>
                </SubCheckboxContainer>
              )}

              {/* <CheckboxItem>
                <Checkbox
                  id="exercise"
                  checked={checkedItems.exercise}
                  onChange={() => handleItemCheck('exercise')}
                />
                <CheckboxLabel htmlFor="exercise">การออกกำลังกาย</CheckboxLabel>
              </CheckboxItem> */}

              {checkedItems.exercise && (
                <SubCheckboxContainer $show={checkedItems.exercise}>
                  <div style={{ padding: '16px 0', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    {/* ประเภทการออกกำลังกาย */}
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>ประเภทการออกกำลังกาย</SubCheckboxLabel>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseType"
                            value="walking"
                            checked={exerciseData.type === 'walking'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, type: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>เดิน</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseType"
                            value="cycling"
                            checked={exerciseData.type === 'cycling'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, type: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ปั่นจักรยาน</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseType"
                            value="running"
                            checked={exerciseData.type === 'running'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, type: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>วิ่ง</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseType"
                            value="fitness"
                            checked={exerciseData.type === 'fitness'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, type: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ฟิตเนส</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseType"
                            value="swimming"
                            checked={exerciseData.type === 'swimming'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, type: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ว่ายน้ำ</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseType"
                            value="other"
                            checked={exerciseData.type === 'other'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, type: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>อื่น (ระบุ)</span>
                        </label>
                      </div>
                      {exerciseData.type === 'other' && (
                        <div style={{ marginTop: '8px' }}>
                          <DateInput
                            type="text"
                            placeholder="ระบุประเภทการออกกำลังกาย"
                            value={exerciseData.otherType}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, otherType: e.target.value }))}
                            style={{ width: '100%' }}
                          />
                        </div>
                      )}
                    </div>

                    {/* ระยะเวลารวม */}
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>ระยะเวลารวม (นาที)</SubCheckboxLabel>
                      <DateInput
                        type="number"
                        placeholder="นาที"
                        value={exerciseData.duration}
                        onChange={(e) => setExerciseData(prev => ({ ...prev, duration: e.target.value }))}
                        style={{ width: '120px' }}
                      />
                    </div>

                    {/* ความเข้มข้นของการออกกำลังกาย */}
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>ความเข้มข้นของการออกกำลังกาย</SubCheckboxLabel>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseIntensity"
                            value="light"
                            checked={exerciseData.intensity === 'light'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, intensity: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>เบา</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseIntensity"
                            value="medium"
                            checked={exerciseData.intensity === 'medium'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, intensity: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>กลาง</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseIntensity"
                            value="hard"
                            checked={exerciseData.intensity === 'hard'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, intensity: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>หนัก</span>
                        </label>
                      </div>
                    </div>

                    {/* ความรู้สึกหลังออกกำลังกาย */}
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>ความรู้สึกหลังออกกำลังกาย</SubCheckboxLabel>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseFeeling"
                            value="refreshed"
                            checked={exerciseData.feeling === 'refreshed'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, feeling: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>สดชื่น</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseFeeling"
                            value="slightly-tired"
                            checked={exerciseData.feeling === 'slightly-tired'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, feeling: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>เหนื่อยเล็กหน่อย</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseFeeling"
                            value="very-tired"
                            checked={exerciseData.feeling === 'very-tired'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, feeling: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>เหนื่อยมาก</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="exerciseFeeling"
                            value="other"
                            checked={exerciseData.feeling === 'other'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, feeling: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>อื่น ๆ (ระบุ)</span>
                        </label>
                      </div>
                      {exerciseData.feeling === 'other' && (
                        <div style={{ marginTop: '8px' }}>
                          <DateInput
                            type="text"
                            placeholder="ระบุความรู้สึก"
                            value={exerciseData.otherFeeling}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, otherFeeling: e.target.value }))}
                            style={{ width: '100%' }}
                          />
                        </div>
                      )}
                    </div>

                    {/* อาการผิดปกติ */}
                    <div>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>อาการผิดปกติ (ถ้ามี)</SubCheckboxLabel>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="abnormalSymptoms"
                            value="none"
                            checked={exerciseData.abnormalSymptoms === 'none'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, abnormalSymptoms: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ไม่มี</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="abnormalSymptoms"
                            value="have"
                            checked={exerciseData.abnormalSymptoms === 'have'}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, abnormalSymptoms: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>มี (ระบุ)</span>
                        </label>
                      </div>
                      {exerciseData.abnormalSymptoms === 'have' && (
                        <div style={{ marginTop: '8px' }}>
                          <DateInput
                            type="text"
                            placeholder="ระบุอาการผิดปกติ"
                            value={exerciseData.abnormalSymptomsDetail}
                            onChange={(e) => setExerciseData(prev => ({ ...prev, abnormalSymptomsDetail: e.target.value }))}
                            style={{ width: '100%' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </SubCheckboxContainer>
              )}

              {/* <CheckboxItem>
                <Checkbox
                  id="sleep"
                  checked={checkedItems.sleep}
                  onChange={() => handleItemCheck('sleep')}
                />
                <CheckboxLabel htmlFor="sleep">การนอนหลับ</CheckboxLabel>
              </CheckboxItem> */}

              {checkedItems.sleep && (
                <SubCheckboxContainer $show={checkedItems.sleep}>
                  <div style={{ padding: '16px 0', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>ระยะเวลาการนอนหลับ (ชั่วโมง)</SubCheckboxLabel>
                      <DateInput
                        type="number"
                        placeholder="8"
                        value={sleepData.duration}
                        onChange={(e) => setSleepData(prev => ({ ...prev, duration: e.target.value }))}
                        style={{ width: '100px' }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>คุณภาพการนอนหลับ</SubCheckboxLabel>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="sleepQuality"
                            value="excellent"
                            checked={sleepData.quality === 'excellent'}
                            onChange={(e) => setSleepData(prev => ({ ...prev, quality: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ดีมาก</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="sleepQuality"
                            value="good"
                            checked={sleepData.quality === 'good'}
                            onChange={(e) => setSleepData(prev => ({ ...prev, quality: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ดี</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="sleepQuality"
                            value="average"
                            checked={sleepData.quality === 'average'}
                            onChange={(e) => setSleepData(prev => ({ ...prev, quality: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ปานกลาง</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="sleepQuality"
                            value="bad"
                            checked={sleepData.quality === 'bad'}
                            onChange={(e) => setSleepData(prev => ({ ...prev, quality: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>แย่</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="sleepQuality"
                            value="very-bad"
                            checked={sleepData.quality === 'very-bad'}
                            onChange={(e) => setSleepData(prev => ({ ...prev, quality: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>แย่มาก</span>
                        </label>
                      </div>
                    </div>
                    
                    <div>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>ปัญหาที่พบ (ถ้ามี)</SubCheckboxLabel>
                      <DateInput
                        type="text"
                        placeholder="ระบุปัญหา"
                        value={sleepData.problems}
                        onChange={(e) => setSleepData(prev => ({ ...prev, problems: e.target.value }))}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </SubCheckboxContainer>
              )}

              {/* <CheckboxItem>
                <Checkbox
                  id="smokeDrink"
                  checked={checkedItems.smokeDrink}
                  onChange={() => handleItemCheck('smokeDrink')}
                />
                <CheckboxLabel htmlFor="smokeDrink">สูบบุหรี่ / ดื่มสุรา</CheckboxLabel>
              </CheckboxItem> */}

              {checkedItems.smokeDrink && (
                <SubCheckboxContainer $show={checkedItems.smokeDrink}>
                  <div style={{ padding: '16px 0', backgroundColor: 'white', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>การดื่มแอลกอฮอล์</SubCheckboxLabel>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="alcohol"
                            value="never"
                            checked={smokeDrinkData.alcohol === 'never'}
                            onChange={(e) => setSmokeDrinkData(prev => ({ ...prev, alcohol: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ไม่เคยดื่ม</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="alcohol"
                            value="occasionally"
                            checked={smokeDrinkData.alcohol === 'occasionally'}
                            onChange={(e) => setSmokeDrinkData(prev => ({ ...prev, alcohol: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ดื่มเป็นบางโอกาส</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="alcohol"
                            value="regularly"
                            checked={smokeDrinkData.alcohol === 'regularly'}
                            onChange={(e) => setSmokeDrinkData(prev => ({ ...prev, alcohol: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ดื่มเป็นประจำ</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="alcohol"
                            value="quit"
                            checked={smokeDrinkData.alcohol === 'quit'}
                            onChange={(e) => setSmokeDrinkData(prev => ({ ...prev, alcohol: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>หยุดดื่ม</span>
                        </label>
                      </div>
                    </div>
                    
                    <div>
                      <SubCheckboxLabel style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>สูบบุหรี่</SubCheckboxLabel>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="smoking"
                            value="never"
                            checked={smokeDrinkData.smoking === 'never'}
                            onChange={(e) => setSmokeDrinkData(prev => ({ ...prev, smoking: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>ไม่เคยสูบ</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="smoking"
                            value="smoke"
                            checked={smokeDrinkData.smoking === 'smoke'}
                            onChange={(e) => setSmokeDrinkData(prev => ({ ...prev, smoking: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>สูบบุหรี่</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="smoking"
                            value="quit"
                            checked={smokeDrinkData.smoking === 'quit'}
                            onChange={(e) => setSmokeDrinkData(prev => ({ ...prev, smoking: e.target.value }))}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: '#00BBD3'
                            }}
                          />
                          <span style={{ fontSize: '14px', fontFamily: "'Noto Sans Thai', sans-serif" }}>หยุดสูบบุหรี่</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </SubCheckboxContainer>
              )}
            </CheckboxGrid>
          </CheckboxSection>

          <CheckboxItemWithSub>
            {/* <CheckboxHeader>
              <Checkbox
                id="cvRisk"
                checked={checkedItems.cvRisk}
                onChange={() => handleItemCheck('cvRisk')}
              />
              <CheckboxLabel htmlFor="cvRisk">ประเมินความเสี่ยงหลอดเลือดสมอง<br />Thai CV risk score แบบไม่ใช้ผลเลือด</CheckboxLabel>
            </CheckboxHeader> */}
            
            {/* {checkedItems.cvRisk && (
              <SubCheckboxContainer $show={checkedItems.cvRisk}>
              <div style={{ padding: '20px 0px', width: '100%', margin: '0', marginBottom: '40px' }}>
                <SectionTitle style={{ fontSize: '22px', fontWeight: '600', color: '#00BBD3', marginBottom: '8px' }}>ประเมินความเสี่ยงหลอดเลือดสมอง</SectionTitle>
                <SectionTitle style={{ fontSize: '18px', color: '#00BBD3', marginBottom: '24px' }}>Thai CV risk score แบบไม่ใช้ผลเลือด</SectionTitle>
                
                <div style={{ display: 'grid', gap: '16px', marginBottom: '32px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                    <span style={{ fontSize: '18px', color: '#666666', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>อายุ</span>
                    <span style={{ fontSize: '18px', color: '#00BBD3', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>45 ปี</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                    <span style={{ fontSize: '18px', color: '#666666', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>เพศ</span>
                    <span style={{ fontSize: '18px', color: '#00BBD3', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>ชาย</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                    <span style={{ fontSize: '18px', color: '#666666', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>สูบบุหรี่อยู่</span>
                    <span style={{ fontSize: '18px', color: '#00BBD3', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>สูบ</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                    <span style={{ fontSize: '18px', color: '#666666', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>เป็นโรคเบาหวาน</span>
                    <span style={{ fontSize: '18px', color: '#00BBD3', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>เป็นโรคเบาหวานชนิดที่ 2</span>
                  </div>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <div style={{ fontSize: '18px', color: '#666666', marginBottom: '16px', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>ความดันโลหิตตัวบน วัดครั้งล่าสุด</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                    <DateInput
                      type="number"
                      value="156"
                      style={{ width: '120px' }}
                      readOnly
                    />
                    <span style={{ fontSize: '18px', color: '#666', fontWeight: 'bold', fontFamily: "'Noto Sans Thai', sans-serif" }}>mmHg</span>
                    <button 
                      style={{
                        backgroundColor: '#00BBD3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '30px',
                        padding: '18px 32px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        fontFamily: "'Noto Sans Thai', sans-serif",
                        boxShadow: '0 9px 14px 0 rgba(0, 187, 211, 0.08)'
                      }}
                    >
                      ดูผล
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <div style={{
                    backgroundColor: '#e6f9ff',
                    border: '2px solid #00BBD3',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{ 
                      fontSize: '20px', 
                      fontWeight: '600', 
                      color: '#00BBD3', 
                      marginBottom: '16px', 
                      fontFamily: "'Noto Sans Thai', sans-serif" 
                    }}>ผลการประเมิน</h3>
                    <p style={{ 
                      fontSize: '16px', 
                      color: '#666666', 
                      lineHeight: '1.6', 
                      margin: '0',
                      fontWeight: 'bold', 
                      fontFamily: "'Noto Sans Thai', sans-serif" 
                    }}>
                      ท่านมีความเสี่ยง โปรดสอบถามผู้เชี่ยวชาญด้านการดูแลสุขภาพของคุณว่าคุณสามารถลดความเสี่ยงได้อย่างไร โรคหลอดเลือดสมองสามารถป้องกัน รักษา และเอาชนะได้เป็นส่วนใหญ่ ถ้าได้รับการดูแลและตรวจสอบอย่างสม่ำเสมอจากผู้เชี่ยวชาญ
                    </p>
                  </div>

                  <div style={{
                    backgroundColor: '#e6f9ff',
                    border: '2px solid #00BBD3',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{ 
                      fontSize: '20px', 
                      fontWeight: '600', 
                      color: '#00BBD3', 
                      marginBottom: '16px', 
                      fontFamily: "'Noto Sans Thai', sans-serif" 
                    }}>ข้อแนะนำเบื้องต้น</h3>
                    <p style={{ 
                      fontSize: '16px', 
                      color: '#666666', 
                      lineHeight: '1.6', 
                      margin: '0',
                      fontWeight: 'bold', 
                      fontFamily: "'Noto Sans Thai', sans-serif" 
                    }}>
                      เพื่อป้องกันการเกิดโรคหลอดเลือดในอนาคตควรออกกำลังกายอย่างสม่ำเสมอ รับประทานผักผลไม้เป็นประจำลดน้ำหนักให้อยู่ในเกณฑ์ปกติ และตรวจสุขภาพประจำปี
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  marginBottom: '32px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#00BBD3',
                    marginBottom: '16px',
                    fontFamily: "'Noto Sans Thai', sans-serif"
                  }}>หมายเหตุ (Disclaimer) การใช้เครื่องมือประเมินความเสี่ยงโรคหัวใจและหลอดเลือด</h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.6',
                    marginBottom: '16px',
                    fontWeight: 'bold',
                    fontFamily: "'Noto Sans Thai', sans-serif"
                  }}>
                    • ผลลัพธ์ที่ได้เป็นการประเมินความเสี่ยงต่อการเจ็บป่วยหรือเสียชีวิตจากโรคเส้นเลือดหัวใจตีบตันและโรคเส้นเลือดสมองตีบตันในระยะเวลา 10 ปีข้างหน้า
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.6',
                    marginBottom: '16px',
                    fontWeight: 'bold',
                    fontFamily: "'Noto Sans Thai', sans-serif"
                  }}>
                    • ผลการประเมินและคำแนะนำที่ได้รับจากโปรแกรมนี้ไม่สามารถใช้แทนการตัดสินใจของแพทย์ได้ การตรวจรักษาเพิ่มเติมหรือการให้ยารักษาขึ้นอยู่กับดุลยพินิจของแพทย์และการปรึกษากันระหว่างแพทย์และตัวท่าน
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.6',
                    marginBottom: '0',
                    fontWeight: 'bold',
                    fontFamily: "'Noto Sans Thai', sans-serif"
                  }}>
                    • ผลการประเมินนี้ห้ามนำไปใช้อ้างอิงในการค้า เช่น การทำประกันชีวิต และไม่สามารถใช้กับผู้ป่วยโรคลิ้นหัวใจหรือโรคหัวใจเต้นผิดจังหวะได้
                  </p>
                </div>
              </div>
              </SubCheckboxContainer>
            )} */}
          </CheckboxItemWithSub>

          <CheckboxItemWithSub>
            {/* <CheckboxHeader>
              <Checkbox
                id="ckdRisk"
                checked={checkedItems.ckdRisk}
                onChange={() => handleItemCheck('ckdRisk')}
              />
              <CheckboxLabel htmlFor="ckdRisk">ประเมินความเสี่ยงโรคไต<br />Thai CKD risk score แบบไม่ใช้ผลเลือด</CheckboxLabel>
            </CheckboxHeader> */}
            
            {checkedItems.ckdRisk && (
              <SubCheckboxContainer $show={checkedItems.ckdRisk}>
              <div style={{ padding: '20px 0px', width: '100%', margin: '0', marginBottom: '40px' }}>
                <SectionTitle style={{ fontSize: '22px', fontWeight: '600', color: '#00BBD3', marginBottom: '8px' }}>ประเมินความเสี่ยงโรคไต</SectionTitle>
                <SectionTitle style={{ fontSize: '18px', color: '#00BBD3', marginBottom: '24px' }}>Thai CKD risk score แบบไม่ใช้ผลเลือด</SectionTitle>
                
                <div style={{ marginBottom: '32px' }}>
                  <div style={{
                    backgroundColor: '#e6f9ff',
                    border: '2px solid #00BBD3',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{ 
                      fontSize: '20px', 
                      fontWeight: '600', 
                      color: '#00BBD3', 
                      marginBottom: '16px', 
                      fontFamily: "'Noto Sans Thai', sans-serif" 
                    }}>ผลการประเมิน</h3>
                    <p style={{ 
                      fontSize: '16px', 
                      color: '#666666', 
                      lineHeight: '1.6', 
                      margin: '0',
                      fontWeight: 'bold', 
                      fontFamily: "'Noto Sans Thai', sans-serif" 
                    }}>
                      ท่านมีความเสี่ยง ปานกลาง ต่อการเกิดโรคไตในระยะเวลา 10 ปี (5%)
                    </p>
                  </div>

                  <div style={{
                    backgroundColor: '#e6f9ff',
                    border: '2px solid #00BBD3',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{ 
                      fontSize: '20px', 
                      fontWeight: '600', 
                      color: '#00BBD3', 
                      marginBottom: '16px', 
                      fontFamily: "'Noto Sans Thai', sans-serif" 
                    }}>ข้อแนะนำเบื้องต้น</h3>
                    <p style={{ 
                      fontSize: '16px', 
                      color: '#666666', 
                      lineHeight: '1.6', 
                      margin: '0',
                      fontWeight: 'bold', 
                      fontFamily: "'Noto Sans Thai', sans-serif" 
                    }}>
                      เพื่อป้องกันการเกิดโรคไตในอนาคต ควรออกกำลังกายอย่างสม่ำเสมอ ดื่มน้ำให้เพียงพอ ลดกินเค็ม ลดน้ำหนักให้อยู่ในเกณฑ์ปกติ
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  marginBottom: '32px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#00BBD3',
                    marginBottom: '16px',
                    fontFamily: "'Noto Sans Thai', sans-serif"
                  }}>หมายเหตุ (Disclaimer) ของการใช้เครื่องมือประเมินความเสี่ยงการเกิดโรคไตเรื้อรัง (Thai CKD Risk Score)</h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.6',
                    marginBottom: '16px',
                    fontWeight: 'bold',
                    fontFamily: "'Noto Sans Thai', sans-serif"
                  }}>
                    • ผลลัพธ์ที่ได้เป็นการประเมินความน่าจะเป็นของโอกาสเกิดโรคไต (อัตราการกรองของไต &lt; 60 มิลลิลิตรนาที/1.73 ตร.ม.) ในระยะเวลา 10 ปีข้างหน้า โดยค่าที่แสดงเป็นการประมาณเบื้องต้นที่อ้างอิงจาก Saranburut et al. BMC Nephrology (2017) 18:240.
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.6',
                    marginBottom: '16px',
                    fontWeight: 'bold',
                    fontFamily: "'Noto Sans Thai', sans-serif"
                  }}>
                    • ผลการประเมินและคำแนะนำที่ได้รับจากโปรแกรมนี้ไม่สามารถใช้แทนการตัดสินใจของแพทย์ได้การตรวจรักษาเพิ่มเติมหรือการให้ยารักษาขึ้นอยู่กับดุลยพินิจของแพทย์และการปรึกษากันโดยตรงระหว่างแพทย์และตัวท่าน
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.6',
                    marginBottom: '0',
                    fontWeight: 'bold',
                    fontFamily: "'Noto Sans Thai', sans-serif"
                  }}>
                    • ผลการประเมินนี้ห้ามนำไปใช้อ้างอิงในการค้า ธุรกิจหรือการทำธุรกรรมเพื่อหวังผลประโยชน์สิ่งใดสิ่งหนึ่ง อาทิเช่น การทำประกันชีวิตและไม่สามารถนำไปใช้กับผู้ป่วยที่ทราบว่าเป็นโรคไตอยู่เดิมได้
                  </p>
                </div>
              </div>
              </SubCheckboxContainer>
            )}
          </CheckboxItemWithSub>

          <SaveButton onClick={handleSave}>
            บันทึก / กลับสู่หน้าหลัก
          </SaveButton>
        </FormSection>
      </Content>
    </DailyHealthRecordContainer>
  );
};

export default DailyHealthRecord;